import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { JubliaAiService } from '@base/services/private/jublia-ai.service';
import { ModalExhibitorComponent } from '@base/components/modal-exhibitor/modal-exhibitor.component';
import { GlobalState } from '@base/models/others/global-state';
import { SearchReason } from '@base/models/others/match-reason';
import { InformaMfeMatchContext } from '@base/models/others/informa-mfe-event';
import { ExhibitorDetail } from '@base/models/responses/exhibitor-detail.res';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { GlobalService } from '@base/services/private/global.service';
import { MeetService } from '@base/services/private/meet.service';
import { SeoService } from '@base/services/private/seo.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';

@Component({
  selector: 'app-exhibitor-detail',
  imports: [],
  templateUrl: './exhibitor-detail.component.html',
  styleUrl: './exhibitor-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExhibitorDetailComponent implements AfterViewInit {
  @ViewChild('exhibitorDetailContainer', { read: ViewContainerRef }) exhibitorDetailContainer!: ViewContainerRef;
  @Input() descriptionMatchQuery: string | null = null;
  private readonly judySrv = inject(JubliaAiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cancelRequests$ = new Subject<void>();
  private descriptionReasons: SearchReason[] = [];
  private isDescriptionMatchLoading = false;

  @Input() modalId = '';
  @Input() exhibitorId: string | null = null;
  // The detail API never returns search_reasons - it only exists on list/search responses -
  // so the caller carries it in from the list item that was clicked.
  @Input() searchReasons: SearchReason[] | null = null;
  @Input() matchContext: InformaMfeMatchContext | null = null;
  @Input() overlayFeedback: (() => void) | null = null;
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly meetSrv = inject(MeetService);
  private readonly seoSrv = inject(SeoService);
  private readonly globalSrv = inject(GlobalService);
  exhibitorDetailInstance: ComponentRef<unknown> | undefined;
  exhibitorDetail: ExhibitorDetail | null = null;
  isShowPlaceholder = true;
  globalState: GlobalState | null = null;

  ngAfterViewInit(): void {
    (async () => {
      await this.loadExhibitorDetailComponent();
    })();
  }

  /**
   * Fetches the exhibitor detail from the server using the provided `exhibitorId`.
   * Updates the state and triggers rendering of the detail component.
   *
   * @returns void
   */
  getExhibitorDetail(): void {
    const exhibitorId = this.exhibitorId;
    if (!exhibitorId || this.destroyRef.destroyed) return;
    this.cancelRequests$.next();
    this.descriptionReasons = [];
    this.exhibitorDetail = null;
    this.isShowPlaceholder = true;
    const query = this.descriptionMatchQuery?.trim();

    if (query) this.getDescriptionMatches(exhibitorId, query);

    this.meetSrv
      .getExhibitorDetail(exhibitorId)
      .pipe(take(1), takeUntil(this.cancelRequests$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (this.exhibitorId !== exhibitorId) return;
          if (!res) {
            this.cancelRequests$.next();
            this.globalSrv.destroyModalRef(this.modalId);
            return;
          }
          this.isShowPlaceholder = false;
          const reasons = this.searchReasons ?? res.search_reasons;
          this.exhibitorDetail = {
            ...res,
            search_reasons: this.descriptionReasons.length ? [...(reasons ?? []), ...this.descriptionReasons] : reasons,
          };
          this.renderExhibitorDetail();
          this.setupExhibitorDetailMetaTags();
        },
        error: (err) => {
          console.error('Error fetching exhibitor details:', err);
          this.cancelRequests$.next();
          this.isShowPlaceholder = false;
          this.globalSrv.destroyModalRef(this.modalId);
        },
      });
  }

  private getDescriptionMatches(exhibitorId: string, query: string): void {
    this.setDescriptionMatchLoading(true);
    this.judySrv
      .getDescriptionMatches({
        entity: 'companies',
        search_query: query,
        description_match_ids: [exhibitorId],
      })
      .pipe(
        take(1),
        takeUntil(this.cancelRequests$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setDescriptionMatchLoading(false))
      )
      .subscribe({
        next: (res) => {
          if (this.exhibitorId !== exhibitorId) return;
          const phrases = res?.description_matches?.[exhibitorId];
          if (!Array.isArray(phrases)) return;
          this.descriptionReasons = phrases
            .filter((phrase): phrase is string => typeof phrase === 'string' && !!phrase.trim())
            .map((phrase) => ({ value: phrase.trim(), field_name: 'description', is_shown_in_listing: false }));
          if (!this.exhibitorDetail || this.descriptionReasons.length === 0) return;
          this.exhibitorDetail = {
            ...this.exhibitorDetail,
            search_reasons: [...(this.exhibitorDetail.search_reasons ?? []), ...this.descriptionReasons],
          };
          const modal = this.exhibitorDetailInstance?.instance as ModalExhibitorComponent | undefined;
          modal?.updateSearchReasons(this.exhibitorDetail.search_reasons ?? []);
        },
        error: (err: unknown) => console.error('Failed to fetch exhibitor description matches:', exhibitorId, err),
      });
  }

  private setDescriptionMatchLoading(isLoading: boolean): void {
    this.isDescriptionMatchLoading = isLoading;
    const modal = this.exhibitorDetailInstance;
    if (!this.destroyRef.destroyed && modal && !modal.hostView?.destroyed) {
      modal.setInput('isDescriptionMatchLoading', isLoading);
    }
  }

  /**
   * Sets up SEO meta tags for the exhibitor detail page.
   */
  setupExhibitorDetailMetaTags() {
    if (!this.exhibitorDetail) return;

    const metaTagObjectArray = [
      {
        property: 'og:url',
        content: String(window.location.href),
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:title',
        content: String(this.exhibitorDetail?.company_name ?? ''),
      },
      {
        property: 'og:description',
        content: String(this.exhibitorDetail?.description ?? ''),
      },
      {
        property: 'og:image',
        content: String(this.exhibitorDetail?.company_banner ?? ''),
      },
    ];

    this.seoSrv.updateMetaTags(metaTagObjectArray);
  }

  /**
   * Dynamically loads the `ModalExhibitor` component into the container.
   *
   * @returns A Promise that resolves when the component is loaded successfully.
   */
  async loadExhibitorDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.exhibitorDetailContainer,
        WhiteLabel.COMPONENTS.ModalExhibitor
      );
      if (this.destroyRef.destroyed) return;
      if (component) {
        this.exhibitorDetailInstance = component.componentRef || undefined;
        if (this.matchContext) {
          this.exhibitorDetailInstance?.setInput('matchContext', this.matchContext);
        }
        if (this.overlayFeedback) {
          this.exhibitorDetailInstance?.setInput('overlayFeedback', this.overlayFeedback);
        }
        this.getExhibitorDetail();
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Passes input values to the loaded `ModalExhibitorComponent` instance.
   *
   * @returns void
   */
  renderExhibitorDetail(): void {
    const component = this.exhibitorDetailInstance?.instance as ModalExhibitorComponent;
    if (component) {
      this.exhibitorDetailInstance?.setInput('exhibitorId', this.exhibitorId);
      this.exhibitorDetailInstance?.setInput('exhibitorDetail', this.exhibitorDetail);
      this.exhibitorDetailInstance?.setInput('isShowPlaceholder', this.isShowPlaceholder);
      this.exhibitorDetailInstance?.setInput('isDescriptionMatchLoading', this.isDescriptionMatchLoading);
      this.exhibitorDetailInstance?.setInput('modalId', this.modalId);
      if (this.matchContext) {
        this.exhibitorDetailInstance?.setInput('matchContext', this.matchContext);
      }
      if (this.overlayFeedback) {
        this.exhibitorDetailInstance?.setInput('overlayFeedback', this.overlayFeedback);
      }
    }
  }
}
